// analytics.js — tiny first-party-safe GA4 wrapper for the PUBLIC MARKETING surfaces only
// (Landing, SegmentPage, Contact, Register + the marketing widgets). Never mounted in the app.
//
// Privacy posture (matches CookieConsent):
//   - Consent + env gated: the gtag script is injected ONLY after an explicit optional-analytics
//     opt-in AND when REACT_APP_GA4_ID is set at build time. Revocation disables the property,
//     clears first-party GA cookies and prevents every subsequent event.
//   - Analytics-only config: anonymize_ip, no Google signals, no ad personalization.
//   - NO PII in any event — props are only utm_*, segment keys, section ids, placements.
//     Names/emails/phones must never be passed to track().
//
// UTM attribution is optional analytics too: it is captured/read only after opt-in.
//
// Observability seams (initialized once from src/index.js, both fail-closed pre-consent):
//   - initWebVitals(): PERF-8 field CWV RUM (web-vitals attribution build, lazy async chunk).
//   - initErrorReporter(): TECH-5 window.__onAppError -> GA4 `exception` + console (ERR- id).

import {
  COOKIE_CONSENT_EVENT,
  COOKIE_CONSENT_KEY,
  hasOptionalAnalyticsConsent,
} from "./cookieConsent";
import { WHATSAPP_URL } from "./contactInfo";

const GA_ID = process.env.REACT_APP_GA4_ID || "";
const UTM_KEY = "vp360_utm";
const UTM_PARAMS = ["utm_source", "utm_medium", "utm_campaign"];

let _initialized = false;
let _listenerAttached = false;
let _gaConfigured = false;
let _consentChangeHandler = null;
let _storageHandler = null;
let _webVitalsStarted = false;
let _errorReporterInstalled = false;

// Pure: parse the supported utm_* params out of a query string ("?utm_source=x&…" or "utm_source=x").
// Values are trimmed + truncated (they ride on every event and into the lead `campaign` field).
export function parseUtm(search) {
  const out = {};
  if (!search || typeof search !== "string") return out;
  let qs;
  try { qs = new URLSearchParams(search.charAt(0) === "?" ? search.slice(1) : search); } catch { return out; }
  for (const k of UTM_PARAMS) {
    const v = (qs.get(k) || "").trim().slice(0, 100);
    if (v) out[k] = v;
  }
  return out;
}

function readStoredUtm() {
  try {
    const raw = window.sessionStorage.getItem(UTM_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    const out = {};
    for (const k of UTM_PARAMS) if (typeof parsed[k] === "string" && parsed[k]) out[k] = parsed[k].slice(0, 100);
    return out;
  } catch { return {}; }
}

function captureUtm() {
  try {
    const found = parseUtm(window.location.search);
    // latest campaign wins; an untagged navigation keeps the stored attribution
    if (Object.keys(found).length) window.sessionStorage.setItem(UTM_KEY, JSON.stringify(found));
  } catch { /* storage unavailable — events still fire, just unattributed */ }
}

function clearStoredUtm() {
  try { window.sessionStorage.removeItem(UTM_KEY); } catch { /* noop */ }
}

/** The captured utm_* attribution (or {}); attached to every event, spreadable anywhere. */
export function utmPayload() {
  if (typeof window === "undefined" || !hasOptionalAnalyticsConsent()) return {};
  return readStoredUtm();
}

// CRO-12 (prepared 2026-08-20, DEFAULT OFF — do not flip without privacy-owner sign-off).
// The open decision: may the lead `campaign` field carry utm_* read from window.location.search
// AT SUBMIT TIME (no storage, no cookies, first-party, page-URL data the server log sees anyway)
// for a visitor who has NOT opted into optional analytics? If the privacy owner rules that this
// is strictly-necessary processing, flip this ONE constant to true and record the decision in
// docs/marketing/CLAIMS_REGISTRY.md + the privacy policy. Both postures are pinned by tests in
// analytics.utm.test.js, so the flip is a one-line reviewed change with no other edits.
// While false, behaviour is byte-identical to the pre-CRO-12 consent gate: no consent -> {}.
export const PRE_CONSENT_UTM_ATTRIBUTION = false;
let _preConsentUtm = PRE_CONSENT_UTM_ATTRIBUTION;
/** Test seam only (the __resetAnalyticsForTests idiom): pins both CRO-12 postures in tests so the
 *  eventual one-line constant flip is already proven. Never call from product code. */
export function __setPreConsentUtmForTests(value) { _preConsentUtm = Boolean(value); }

/** UTM folded into the backend's EXISTING whitelisted lead field. /public/platform-enquiry
 *  strictly whitelists its stored keys, so arbitrary utm_* keys would be dropped — but it DOES
 *  store `campaign` (80 chars). Returns { campaign: "utm:src/med/camp" } or {} — spread it into
 *  a lead POST body BEFORE any caller-owned keys so it can never clobber them.
 *
 *  Attribution sources, in order:
 *    1. consented: the sessionStorage capture (latest tagged navigation wins);
 *    2. pre-consent AND PRE_CONSENT_UTM_ATTRIBUTION: a transient parse of the CURRENT URL at
 *       submit time — nothing is stored, nothing persists past this call (CRO-12's approved
 *       shape: "window.location.search at form-submit time (no storage)"). */
export function utmLeadFields() {
  let u = utmPayload();
  if (!UTM_PARAMS.some((k) => u[k]) && _preConsentUtm && typeof window !== "undefined" && !hasOptionalAnalyticsConsent()) {
    u = parseUtm(window.location.search);
  }
  if (!UTM_PARAMS.some((k) => u[k])) return {};
  const campaign = `utm:${u.utm_source || "-"}/${u.utm_medium || "-"}/${u.utm_campaign || "-"}`.slice(0, 80);
  return { campaign };
}

function consentPayload(analyticsStorage) {
  return {
    analytics_storage: analyticsStorage,
    ad_storage: "denied",
    ad_user_data: "denied",
    ad_personalization: "denied",
  };
}

function startAnalytics() {
  if (!GA_ID || !hasOptionalAnalyticsConsent()) return;
  try {
    window[`ga-disable-${GA_ID}`] = false;
    window.dataLayer = window.dataLayer || [];
    if (typeof window.gtag !== "function") {
      window.gtag = function gtag() { window.dataLayer.push(arguments); };
    }
    window.gtag("consent", "update", consentPayload("granted"));
    if (!_gaConfigured) {
      window.gtag("js", new Date());
      window.gtag("config", GA_ID, {
        anonymize_ip: true,
        allow_google_signals: false,
        allow_ad_personalization_signals: false,
        // WP-06: this is an SPA — the automatic config-time page_view would count ONLY the
        // first document. MarketingAnalytics fires an explicit page_view per route change
        // (including the initial one), so the automatic send is off to avoid double-counting.
        send_page_view: false,
      });
      _gaConfigured = true;
    }
    if (!document.querySelector('script[data-vp-gtag]')) {
      const s = document.createElement("script");
      s.async = true;
      s.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(GA_ID)}`;
      s.setAttribute("data-vp-gtag", "1");
      document.head.appendChild(s);
    }
  } catch { /* analytics must never break the page */ }
}

// ---------------------------------------------------------------------------
// Microsoft Clarity (WP-06) — session replay/heatmaps. Same consent + env gate as GA4:
// injected ONLY after explicit optional-analytics opt-in AND a build-time project id.
// ---------------------------------------------------------------------------
const CLARITY_ID = process.env.REACT_APP_CLARITY_ID || "";

function startClarity() {
  if (!CLARITY_ID || !hasOptionalAnalyticsConsent()) return;
  try {
    if (document.querySelector("script[data-vp-clarity]")) {
      if (typeof window.clarity === "function") window.clarity("consent");
      return;
    }
    window.clarity = window.clarity || function clarityQueue(...args) {
      (window.clarity.q = window.clarity.q || []).push(args);
    };
    window.clarity("consent"); // cookie use is consented before the tag boots
    const s = document.createElement("script");
    s.async = true;
    s.src = `https://www.clarity.ms/tag/${encodeURIComponent(CLARITY_ID)}`;
    s.setAttribute("data-vp-clarity", "1");
    document.head.appendChild(s);
  } catch { /* analytics must never break the page */ }
}

function clearClarityCookies() {
  try {
    for (const part of document.cookie.split(";")) {
      const name = part.split("=")[0]?.trim();
      if (!/^(_clck|_clsk|CLID|MUID)$/i.test(name || "")) continue;
      document.cookie = `${name}=; Max-Age=0; path=/; SameSite=Lax`;
      if (window.location.hostname) {
        document.cookie = `${name}=; Max-Age=0; path=/; domain=${window.location.hostname}; SameSite=Lax`;
      }
    }
  } catch { /* best-effort cleanup */ }
}

function stopClarity() {
  if (!CLARITY_ID) return;
  try {
    if (typeof window.clarity === "function") window.clarity("stop");
    clearClarityCookies();
  } catch { /* analytics must never break the page */ }
}

function clearGaCookies() {
  try {
    for (const part of document.cookie.split(";")) {
      const name = part.split("=")[0]?.trim();
      if (!/^_ga(?:_|$)/.test(name || "")) continue;
      document.cookie = `${name}=; Max-Age=0; path=/; SameSite=Lax`;
      if (window.location.hostname) {
        document.cookie = `${name}=; Max-Age=0; path=/; domain=${window.location.hostname}; SameSite=Lax`;
      }
    }
  } catch { /* best-effort cleanup; the disable flag still stops collection */ }
}

function stopAnalytics() {
  clearStoredUtm();
  if (!GA_ID) return;
  try {
    window[`ga-disable-${GA_ID}`] = true;
    if (typeof window.gtag === "function") {
      window.gtag("consent", "update", consentPayload("denied"));
    }
    clearGaCookies();
  } catch { /* analytics must never break the page */ }
}

function applyConsent() {
  // Never trust event detail by itself: only a currently valid, persisted, unexpired choice can
  // start collection. This also makes synthetic same-document events fail closed.
  const analyticsAllowed = hasOptionalAnalyticsConsent();
  if (analyticsAllowed) {
    captureUtm();
    startAnalytics();
    startClarity();
  } else {
    stopAnalytics();
    stopClarity();
  }
}

function listenForConsent() {
  if (_listenerAttached) return;
  _consentChangeHandler = applyConsent;
  _storageHandler = (event) => {
    if (event.key === COOKIE_CONSENT_KEY) applyConsent();
  };
  window.addEventListener(COOKIE_CONSENT_EVENT, _consentChangeHandler);
  window.addEventListener("storage", _storageHandler);
  _listenerAttached = true;
}

// ---------------------------------------------------------------------------
// PERF-8 — consent-gated Core Web Vitals field RUM (the `web-vitals` ATTRIBUTION build).
// Field LCP/CLS/INP finally get measured for the real (mid-Android, Indian-network) audience:
// each finalized metric reports through the SAME consent gate as every other event (`track()`
// no-ops until explicit optional-analytics opt-in — no events before consent), with route +
// element attribution per the web.dev GA4 recipe. The library loads as a lazy async chunk at
// idle (never in the eager entry bundle the 300 KB budget gates); PerformanceObserver's buffered
// entries make idle-time registration lossless for LCP/CLS/INP.
// ---------------------------------------------------------------------------

// RUM covers the PUBLIC marketing/auth surface only. The signed-in product and the payer/portal
// surfaces never report (this wrapper is marketing-only by contract; consent UI exists only on
// marketing routes anyway — this guard just keeps a consented session from leaking /app paths
// into the marketing GA4 property after login).
function rumEligiblePath(pathname) {
  return !/^\/(app|parent|student|pay)(\/|$)/.test(pathname) &&
    !/^\/platform(\/(?!login$)|$)/.test(pathname);
}

function reportWebVital(metric) {
  try {
    if (!hasOptionalAnalyticsConsent()) return; // fail-closed: nothing before opt-in
    const pagePath = window.location.pathname;
    if (!rumEligiblePath(pagePath)) return;
    const attribution = metric.attribution || {};
    const debugTarget =
      metric.name === "CLS" ? attribution.largestShiftTarget
        : metric.name === "INP" ? attribution.interactionTarget
          : attribution.element; // LCP
    track(metric.name, {
      // GA4 sums `value`, so send the DELTA (web.dev guidance); the full value rides alongside.
      value: metric.delta,
      metric_id: metric.id,
      metric_value: metric.value,
      metric_delta: metric.delta,
      metric_rating: metric.rating,
      page_path: pagePath,
      ...(debugTarget ? { debug_target: String(debugTarget).slice(0, 100) } : {}),
      ...(metric.name === "LCP" && attribution.url ? { debug_url: String(attribution.url).slice(0, 150) } : {}),
    });
  } catch { /* observability must never break the page */ }
}

/** Idempotent, SSR-safe. Called once from src/index.js; safe to call before any consent choice —
 *  metrics observed pre-consent are simply never reported. */
export function initWebVitals() {
  if (typeof window === "undefined" || typeof document === "undefined" || _webVitalsStarted) return;
  _webVitalsStarted = true;
  const start = () => {
    import(/* webpackChunkName: "web-vitals" */ "web-vitals/attribution")
      .then(({ onLCP, onCLS, onINP }) => {
        onLCP(reportWebVital);
        onCLS(reportWebVital);
        onINP(reportWebVital);
      })
      .catch(() => { /* RUM is best-effort */ });
  };
  if (typeof window.requestIdleCallback === "function") window.requestIdleCallback(start, { timeout: 3000 });
  else window.setTimeout(start, 1500);
}

// ---------------------------------------------------------------------------
// TECH-5 — first-party error observability for the marketing surface. ErrorBoundary already
// calls `window.__onAppError({ id, error, info })` when a render error is caught (the hook was
// unwired). Wire it to a consent-aware GA4 `exception` event (the standard GA4 error shape —
// alertable per route in GA4/BigQuery) plus one compact first-party console line carrying the
// shareable ERR- id. No new backend, no PII: the payload is the error id + the error's own
// message, truncated.
// ---------------------------------------------------------------------------
export function initErrorReporter() {
  if (typeof window === "undefined" || _errorReporterInstalled) return;
  _errorReporterInstalled = true;
  const previous = window.__onAppError;
  window.__onAppError = (payload = {}) => {
    if (typeof previous === "function") {
      try { previous(payload); } catch { /* never let a chained hook break reporting */ }
    }
    try {
      const id = payload.id || "ERR-UNKNOWN";
      const message = String((payload.error && (payload.error.message || payload.error)) || "render error");
      // Compact, correlatable first-party record (ErrorBoundary logs the full stack separately).
      // eslint-disable-next-line no-console
      console.error(`[app-error] ${id} on ${window.location.pathname}: ${message.slice(0, 200)}`);
      track("exception", {
        description: `${id}: ${message}`.slice(0, 150),
        fatal: true,
        page_path: window.location.pathname,
      });
    } catch { /* observability must never break the page */ }
  };
}

// Test-only lifecycle reset. Kept explicit so module-state idempotence and event-listener cleanup
// can be verified without relying on Jest module reload semantics.
export function __resetAnalyticsForTests() {
  if (typeof window !== "undefined" && _listenerAttached) {
    window.removeEventListener(COOKIE_CONSENT_EVENT, _consentChangeHandler);
    window.removeEventListener("storage", _storageHandler);
  }
  _initialized = false;
  _listenerAttached = false;
  _gaConfigured = false;
  _consentChangeHandler = null;
  _storageHandler = null;
  _webVitalsStarted = false;
  _errorReporterInstalled = false;
  _preConsentUtm = PRE_CONSENT_UTM_ATTRIBUTION;
}

/** Idempotent, SSR-safe init. Provider setup and UTM capture remain off until explicit opt-in. */
export function init() {
  if (typeof window === "undefined" || typeof document === "undefined") return;
  if (_initialized) return;
  _initialized = true;
  listenForConsent();
  if (hasOptionalAnalyticsConsent()) {
    captureUtm();
    startAnalytics();
    startClarity();
  } else {
    stopAnalytics();
    stopClarity();
  }
}

/** WP-06: explicit SPA page_view — MarketingAnalytics calls this on every route change
 *  (initial load included; `send_page_view:false` above prevents double counting). */
export function trackPageView(pagePath) {
  if (typeof window === "undefined" || !hasOptionalAnalyticsConsent()) return false;
  try {
    const payload = {
      page_path: pagePath,
      page_location: window.location.href,
      page_title: document.title,
      ...utmPayload(),
    };
    if (GA_ID && typeof window.gtag === "function") window.gtag("event", "page_view", payload);
    else console.debug("[analytics] page_view", payload);
    return true;
  } catch { /* never break the UI for analytics */ }
  return false;
}

/** WP-06: canonical conversion event for WhatsApp click-to-chat anchors. */
export function trackWhatsAppClick(placement) {
  return track("whatsapp_click", { placement });
}

/** CRO-10: offline-channel intent — a tel: line was clicked. `line` is the line's labelled
 *  purpose ("sales" / "onboarding" / "support"), never the phone number (no PII in props). */
export function trackPhoneCallClick(placement, line) {
  return track("phone_call_click", { placement, line });
}

/** CRO-11: WhatsApp click-to-chat with a context prefill — the WhatsApp-first ICP lands in the
 *  chat with the evaluation context already typed. `pageLabel` is a short human page name
 *  ("the Contact page"), never PII; the visitor sees and can edit the text before sending. */
export function whatsappUrlWithContext(pageLabel) {
  const text = `Hi, I'm evaluating Vidyapeeth360 for my school${pageLabel ? ` (from ${pageLabel})` : ""}.`;
  return `${WHATSAPP_URL}?text=${encodeURIComponent(text)}`;
}

/** Fire an event (UTM attribution auto-attached). Returns false until analytics is allowed. */
export function track(event, props = {}) {
  if (typeof window === "undefined" || !event || !hasOptionalAnalyticsConsent()) return false;
  try {
    const payload = { ...utmPayload(), ...props };
    if (GA_ID && typeof window.gtag === "function") window.gtag("event", event, payload);
    else console.debug(`[analytics] ${event}`, payload);
    return true;
  } catch { /* never break the UI for analytics */ }
  return false;
}

/** Fire `event` ONCE when the element with `elementId` first scrolls into view.
 *  Returns a cleanup fn (safe for useEffect). Tolerates the element mounting late
 *  (CMS-gated sections) and environments without IntersectionObserver. */
export function trackOnView(elementId, event, props = {}) {
  if (typeof window === "undefined" || typeof document === "undefined" ||
      typeof IntersectionObserver === "undefined") return () => {};
  let observer = null;
  let timer = null;
  let tries = 0;
  let fired = false;
  const attach = () => {
    timer = null;
    const el = document.getElementById(elementId);
    if (!el) {
      if (tries++ < 10) timer = setTimeout(attach, 700); // section may render after the CMS fetch
      return;
    }
    observer = new IntersectionObserver((entries) => {
      if (fired || !entries.some((e) => e.isIntersecting)) return;
      fired = true;
      track(event, { section_id: elementId, ...props });
      observer.disconnect();
    }, { threshold: 0.25 });
    observer.observe(el);
  };
  attach();
  return () => {
    if (timer) clearTimeout(timer);
    if (observer) observer.disconnect();
  };
}
